import React, { useCallback, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormGroup,
  FormControlLabel,
  Input,
  Grid,
  TextField,
  makeStyles,
  Typography,
  Divider
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import useApiAlert from "src/hooks/useApiAlert";
import { yupTrimToNull } from "src/utils/stringUtils";
import { IRemyndBrandContactSaveReq } from "../types";

const useStyles = makeStyles(_ => ({
  container: {
    "& > *": {
      padding: "12px 6px"
    }
  },
  formLabel: {
    marginBottom: "4px"
  }
}));

interface Props {
  label: string;
  open: boolean;
  defaultValue?: IRemyndBrandContactSaveReq;
  handleCloseModal: () => void;
  handleSubmitModal: (value: IRemyndBrandContactSaveReq) => void;
}

const schema = yup.object().shape({
  id: yup.string(),
  labelTh: yup
    .string()
    .transform(yupTrimToNull)
    .required("กรุณาระบุ Label (TH)"),
  labelEn: yup
    .string()
    .transform(yupTrimToNull)
    .required("กรุณาระบุ Label (EN)"),
  addressTh: yup.string().transform(yupTrimToNull),
  addressEn: yup.string().transform(yupTrimToNull),
  phoneNumber: yup.string().transform(yupTrimToNull),
  email: yup.string().transform(yupTrimToNull),
  active: yup.boolean()
});

type FormData = Omit<IRemyndBrandContactSaveReq, "latitude" | "longitude"> & {
  latitude?: string;
  longitude?: string;
};

const defaultLabels = [
  { th: "Call Center", en: "Call Center" },
  { th: "ศูนย์บริการ", en: "Service" },
  { th: "ศูนย์ซ่อม", en: "Repair Center" },
  { th: "ที่อยู่", en: "Address" },
  { th: "สำนักงาน", en: "Office" },
  { th: "บริการลูกค้า", en: "Customer Service" },
  { th: "สายด่วน", en: "Hot Line" },
  { th: "โชว์รูม", en: "Showroom" },
  { th: "ร้านค้า", en: "Shop" }
];

const BrandContactModal: React.FC<Props> = props => {
  const classes = useStyles();
  const { setMessage } = useApiAlert();
  const { open, label, defaultValue, handleCloseModal, handleSubmitModal } =
    props;

  const defaultFormData = useMemo<Partial<FormData>>(
    () => ({
      ...defaultValue,
      latitude: defaultValue?.latitude?.toString(),
      longitude: defaultValue?.longitude?.toString(),
      active: defaultValue?.active ?? true
    }),
    [defaultValue]
  );

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue
  } = useForm<FormData>({
    defaultValues: defaultFormData,
    resolver: yupResolver(schema)
  });

  const handlePasteCoord = useCallback(async () => {
    try {
      const str = await navigator.clipboard.readText();
      const re = new RegExp("[\\-]?\\d{1,2}\\.\\d+\\s*,\\s*\\d{1,3}\\.\\d+");
      if (re.test(str)) {
        const tokens = str.split(",");
        const lat = tokens[0].trim();
        const lng = tokens[1].trim();
        setValue("latitude", lat);
        setValue("longitude", lng);
        setMessage("success", `Pasted coordinate: ${str}`);
      } else {
        setMessage("error", `Invalid format: ${str}`);
      }
    } catch (e) {
      setMessage("error", `Error reading clipboard: ${e}`);
    }
  }, [setValue, setMessage]);

  const handleTransformAndSubmit = (form: FormData) => {
    const latitude = parseFloat(`${form.latitude}`);
    const longitude = parseFloat(`${form.longitude}`);
    handleSubmitModal({
      ...form,
      ord: 0, // will be set at final submit
      latitude: isNaN(latitude) ? undefined : latitude,
      longitude: isNaN(longitude) ? undefined : longitude
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="md"
      fullWidth
      scroll="body"
      aria-labelledby="form-dialog-title"
    >
      <form onSubmit={handleSubmit(handleTransformAndSubmit)}>
        <DialogTitle id="form-dialog-title" disableTypography>
          <Typography variant="h3">
            {!defaultFormData.id ? "เพิ่ม " : "แก้ไข "}
            {label}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Input
            type="hidden"
            {...register("id")}
            defaultValue={defaultFormData.id}
          />
          <Grid container className={classes.container}>
            <Grid item xs={12} md={6}>
              <Controller
                name="labelTh"
                control={control}
                rules={{ required: true }}
                defaultValue={defaultFormData.labelTh ?? ""}
                render={({ field }) => (
                  <Autocomplete
                    freeSolo
                    options={defaultLabels.map(it => it.th)}
                    fullWidth
                    renderInput={params => (
                      <TextField
                        variant="outlined"
                        {...params}
                        label="Label (TH) *"
                        error={Boolean(errors.labelTh)}
                        helperText={errors.labelTh?.message}
                        onChange={field.onChange}
                      />
                    )}
                    {...field}
                    onChange={(e, value) => {
                      field.onChange(value);
                      const match = defaultLabels.find(v => v.th === value);
                      if (match) {
                        setValue("labelEn", match.en);
                      }
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="labelEn"
                control={control}
                rules={{ required: true }}
                defaultValue={defaultFormData.labelEn ?? ""}
                render={({ field }) => (
                  <Autocomplete
                    freeSolo
                    options={defaultLabels.map(it => it.en)}
                    fullWidth
                    renderInput={params => (
                      <TextField
                        variant="outlined"
                        {...params}
                        label="Label (EN) *"
                        error={Boolean(errors.labelEn)}
                        helperText={errors.labelEn?.message}
                        onChange={field.onChange}
                      />
                    )}
                    {...field}
                    onChange={(e, value) => {
                      field.onChange(value);
                      const match = defaultLabels.find(v => v.en === value);
                      if (match) {
                        setValue("labelTh", match.th);
                      }
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="addressTh"
                control={control}
                defaultValue={defaultFormData.addressTh ?? ""}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={2}
                    rowsMax={8}
                    {...field}
                    label="Address (TH)"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="addressEn"
                control={control}
                defaultValue={defaultFormData.addressEn ?? ""}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={2}
                    rowsMax={8}
                    {...field}
                    label="Address (EN)"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider light />
            </Grid>

            <Grid item xs={6} md={3}>
              <Controller
                name="latitude"
                control={control}
                defaultValue={defaultFormData.latitude ?? ""}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    {...field}
                    label="Latitude"
                    error={Boolean(errors.latitude)}
                    helperText={errors.latitude?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <Controller
                name="longitude"
                control={control}
                defaultValue={defaultFormData.longitude ?? ""}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    {...field}
                    label="Longitude"
                    error={Boolean(errors.longitude)}
                    helperText={errors.longitude?.message}
                  />
                )}
              />
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              style={{ display: "flex", alignItems: "center" }}
            >
              <Button color="primary" onClick={handlePasteCoord}>
                Paste Google Maps Coordinate
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="email"
                control={control}
                defaultValue={defaultFormData.email ?? ""}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    {...field}
                    label="Email"
                    helperText={`คั่นด้วย comma (,) หากมีหลาย Email`}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="phoneNumber"
                control={control}
                defaultValue={defaultFormData.phoneNumber ?? ""}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    {...field}
                    label="Phone Number"
                    helperText={`คั่นด้วย comma (,) หากมีหลายเบอร์`}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider light />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Controller
                      name="active"
                      control={control}
                      defaultValue={defaultFormData.active ?? false}
                      render={({ field: { value, onChange } }) => (
                        <Checkbox
                          checked={value}
                          onChange={onChange}
                          color="primary"
                        />
                      )}
                    />
                  }
                  label="Active"
                />
              </FormGroup>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Cancel
          </Button>
          {!defaultValue ? (
            <Button
              key="addBtn"
              variant="contained"
              color="primary"
              type="submit"
            >
              Add
            </Button>
          ) : (
            <Button
              key="updateBtn"
              variant="contained"
              color="primary"
              type="submit"
            >
              Update
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default BrandContactModal;
